import { useEffect, useRef, useCallback, useState } from 'react';
import { api } from '../services/api';

/**
 * Per-session SSE connection via POST — on-demand.
 * Receives: card ops, task lifecycle, heartbeat
 * Supports last_event_id reconnection for gapless replay.
 *
 * Uses fetch() + ReadableStream instead of EventSource to support POST
 * (EventSource only supports GET). The unified POST /sessions/{id}/stream
 * endpoint handles both SSE streaming and card actions via body-based routing.
 *
 * Card operations (op-based): create_card, stream_to_card, update_card,
 *   append_to_card, replace_card, finalize_card, remove_card
 * Task lifecycle: exec_start, exec_progress, exec_result, exec_error
 *
 * @param {string} viUserId - The VI user ID
 * @param {string} sessionId - The session to subscribe to
 * @param {{ onEvent?: function, onConnected?: function, onFirstByte?: function }} callbacks
 * @returns {{ connected: boolean }}
 */
export function useSessionEventStream(viUserId, sessionId, { onEvent, onConnected, onFirstByte } = {}) {
  const [connected, setConnected] = useState(false);
  const abortRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const lastEventIdRef = useRef(null);
  const mountedRef = useRef(true);
  // Stable ref for the callback to avoid effect re-runs
  const onEventRef = useRef(onEvent);
  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);
  const onConnectedRef = useRef(onConnected);
  useEffect(() => { onConnectedRef.current = onConnected; }, [onConnected]);
  const onFirstByteRef = useRef(onFirstByte);
  useEffect(() => { onFirstByteRef.current = onFirstByte; }, [onFirstByte]);

  const EVENT_TYPES = new Set([
    'create_card', 'stream_to_card', 'update_card', 'append_to_card',
    'replace_card', 'finalize_card', 'remove_card',
    // media_fill: a deferred (fire-and-continue) image/video resolved → swap the
    // in-card `data-vi-media="pending"` slot in place. Handled by the app + the
    // api-server snapshot reducer; without this allowlist entry (+ the store case)
    // the web LIVE SSE dropped it → the image only appeared after a reload (beta b8dff2b0).
    'media_fill',
    // media_progress: the NON-TERMINAL, slot-keyed tick the runner forwards from
    // the generator's own poll (CONTRACT §5) — `{slot, stage, pct?, label?}` on a
    // still-pending native-ir block. Dropping it here would cost only the live
    // decoration (the terminal media_fill/replace_card still lands), but the wait
    // would stay a mute "Generating…" for the whole job. Native-ir only: an html
    // turn carries no block card with the slot, so the store's case is a no-op.
    'media_progress',
    'exec_start', 'exec_progress', 'exec_result', 'exec_error', 'exec_stopped',
    'exec_timeout', 'exec_queued', 'exec_heartbeat',
    // #2661: per-turn user message marker for multi-turn (mid-stream inject)
    // sessions — lets SSE-replay reconstruct the user bubbles the WAL otherwise
    // only carries as card ops. Without this allowlist entry the frame is
    // dropped before reaching onEvent and SSE-replay shows no user inputs.
    'user_turn',
    'heartbeat',
  ]);

  // Close existing connection
  const closeConnection = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    queueMicrotask(() => {
      if (mountedRef.current) setConnected(false);
    });
  }, []);

  const connect = useCallback(async () => {
    if (!viUserId || !sessionId) return;

    // Abort any existing connection
    if (abortRef.current) abortRef.current.abort();

    const controller = new AbortController();
    abortRef.current = controller;
    // IMPORTANT: capture this connection's controller in a local so the dispatch
    // loop below can check IT (not abortRef.current). After a session switch,
    // abortRef.current will point at the NEW controller, and the previous
    // connection's still-draining dispatch loop would otherwise see "not aborted"
    // and leak buffered events from session A into session B's processEvent.
    // Do not "simplify" this back to abortRef.current.
    const myController = controller;

    const apiUrl = api.getBaseUrl();
    const url = `${apiUrl}/api/users/sessions/${encodeURIComponent(sessionId)}/stream?format=signed`;

    try {
      const authHeaders = await api.getAuthHeaders();
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          last_event_id: lastEventIdRef.current || undefined,
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      reconnectAttemptRef.current = 0;
      if (mountedRef.current) setConnected(true);
      onConnectedRef.current?.({ sessionId, ts: Date.now() });
      console.log(`[SSE:session] connected to session ${sessionId}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let sawFirstByte = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (!sawFirstByte) {
          sawFirstByte = true;
          onFirstByteRef.current?.({ sessionId, ts: Date.now() });
        }
        buffer += decoder.decode(value, { stream: true });

        // Parse SSE frames from buffer
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';

        // Collect events from this chunk, then dispatch asynchronously.
        // EventSource dispatches each event as a separate browser task;
        // we must match that to avoid React batching all WAL replay events
        // into a single state update (which breaks card state rebuilding).
        const events = [];
        for (const frame of parts) {
          if (!frame.trim()) continue;

          let eventType = 'message';
          let eventId = null;
          let dataStr = '';

          for (const line of frame.split('\n')) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7);
            } else if (line.startsWith('id: ')) {
              eventId = line.slice(4);
            } else if (line.startsWith('data: ')) {
              dataStr += line.slice(6);
            }
          }

          if (eventId) {
            lastEventIdRef.current = eventId;
          }

          if (eventType === 'heartbeat') continue;

          if (EVENT_TYPES.has(eventType) && dataStr) {
            try {
              const parsed = JSON.parse(dataStr);
              // Card ops use `op` field, task lifecycle uses `type` field
              const key = parsed.op ? 'op' : 'type';
              events.push({ [key]: eventType, ...parsed, _ts: Date.now() });
              console.log(`[SSE:session] \u2190 ${eventType} id=${eventId}`);
            } catch { /* skip unparseable */ }
          }
        }

        // Dispatch events one per macrotask to match EventSource behavior.
        // Check the captured `myController` (NOT abortRef.current) — see the
        // comment at the top of connect(). This guards against cross-session leaks.
        for (const event of events) {
          await new Promise(r => setTimeout(r, 0));
          if (myController.signal.aborted) return;
          onEventRef.current?.(event);
        }
      }

      // Stream ended cleanly — server closed connection
      console.log(`[SSE:session] disconnected from session ${sessionId}`);
      if (mountedRef.current) scheduleReconnect();
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.log(`[SSE:session] error:`, err.message || err);
      if (mountedRef.current) {
        setConnected(false);
        scheduleReconnect();
      }
    }
  }, [viUserId, sessionId]);

  const scheduleReconnect = useCallback(() => {
    const delay = Math.min(1000 * Math.pow(2, reconnectAttemptRef.current), 30000);
    console.log(`[SSE:session] reconnecting (attempt ${reconnectAttemptRef.current + 1}, backoff ${delay}ms)`);
    reconnectTimerRef.current = setTimeout(() => {
      reconnectAttemptRef.current++;
      connect();
    }, delay);
  }, [connect]);

  useEffect(() => {
    mountedRef.current = true;
    // Reset lastEventId on fresh connection (session switch / remount)
    // so WAL replays from 0. Reconnections bypass this effect and preserve the ID.
    lastEventIdRef.current = null;
    if (viUserId && sessionId) {
      connect();
    } else {
      closeConnection();
    }
    return () => {
      closeConnection();
    };
  }, [viUserId, sessionId, connect, closeConnection]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      closeConnection();
    };
  }, [closeConnection]);

  return { connected };
}
